import React, { useState, useEffect } from 'react';
import { Search, Music, Play, Pause, Clock, User, Trash2, X, ExternalLink, BarChart3, ArrowRight } from 'lucide-react';
import axios from 'axios';

interface MusicMessage {
    id: number;
    sender_id: number;
    sender_username: string;
    sender_name?: string;
    receiver_id: number;
    receiver_username: string;
    receiver_name?: string;
    track_id: string;
    track_name: string;
    track_artist: string;
    track_album?: string;
    image_url?: string;
    preview_url?: string;
    external_url?: string;
    message?: string;
    is_approved: boolean;
    is_played: boolean;
    created_at: string;
    played_at?: string;
}

interface MusicStats {
    total_messages: number;
    period_messages: number;
    played_messages: number;
    approved_messages: number;
    play_rate: number;
    top_artists: { artist: string; count: number }[];
    top_senders: { user_id: number; username: string; full_name?: string; count: number }[];
}

// Stat Card
const StatCard: React.FC<{ icon: React.ReactNode; value: number | string; label: string; color: string }> = ({ icon, value, label, color }) => {
    const colors: Record<string, string> = {
        green: 'from-green-50 to-green-100 border-green-200 text-green-700',
        blue: 'from-blue-50 to-blue-100 border-blue-200 text-blue-700',
        purple: 'from-purple-50 to-purple-100 border-purple-200 text-purple-700',
        orange: 'from-orange-50 to-orange-100 border-orange-200 text-orange-700',
    };

    return (
        <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-2.5 text-center`}>
            <div className="flex justify-center mb-1 opacity-60 [&>svg]:w-4 [&>svg]:h-4">{icon}</div>
            <p className="text-xl font-semibold">{value}</p>
            <p className="text-[10px] opacity-60 uppercase tracking-wide">{label}</p>
        </div>
    );
};

const MusicManagement: React.FC = () => {
    const [messages, setMessages] = useState<MusicMessage[]>([]);
    const [stats, setStats] = useState<MusicStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<MusicMessage | null>(null);
    const [actionLoading, setActionLoading] = useState<number | null>(null);
    const [audioPlaying, setAudioPlaying] = useState<number | null>(null);
    const [audioRef, setAudioRef] = useState<HTMLAudioElement | null>(null);
    const [limit, setLimit] = useState(50);

    useEffect(() => {
        fetchMessages();
        fetchStats();
    }, []);

    const fetchMessages = async () => {
        try {
            const r = await axios.get('https://api.lifeshare.app/api/v1/admin/music');
            setMessages(r.data.messages || []);
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    const fetchStats = async () => {
        try {
            const r = await axios.get('https://api.lifeshare.app/api/v1/admin/music/stats?days=30');
            setStats(r.data);
        } catch (e) { console.error(e); }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Bu müzik mesajını silmek istiyor musunuz?')) return;
        setActionLoading(id);
        try {
            await axios.delete(`https://api.lifeshare.app/api/v1/admin/music/${id}`);
            setMessages(messages.filter(m => m.id !== id));
            if (selected?.id === id) setSelected(null);
        } catch (e) { alert('Silme işlemi başarısız'); }
        setActionLoading(null);
    };

    const playPreview = (id: number, url: string) => {
        if (audioPlaying === id) {
            audioRef?.pause();
            setAudioPlaying(null);
        } else {
            audioRef?.pause();
            const audio = new Audio(url);
            audio.play();
            audio.onended = () => setAudioPlaying(null);
            setAudioRef(audio);
            setAudioPlaying(id);
        }
    };

    const filtered = messages.filter(m =>
        m.track_name.toLowerCase().includes(search.toLowerCase()) ||
        m.track_artist.toLowerCase().includes(search.toLowerCase()) ||
        m.sender_username.toLowerCase().includes(search.toLowerCase()) ||
        m.receiver_username.toLowerCase().includes(search.toLowerCase())
    );

    if (loading) return (
        <div className="flex items-center justify-center py-20">
            <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
        </div>
    );

    return (
        <div className="space-y-6">
            <div className="max-w-6xl mx-auto">

                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Müzik</h1>
                    <p className="text-sm text-gray-500 mt-1">{messages.length} müzik paylaşımı</p>
                </div>

                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                        <StatCard icon={<Music />} value={stats.total_messages} label="Toplam" color="purple" />
                        <StatCard icon={<BarChart3 />} value={stats.period_messages} label="30 Gün" color="blue" />
                        <StatCard icon={<Play />} value={stats.played_messages} label="Dinlenen" color="green" />
                        <StatCard icon={<Clock />} value={`${stats.play_rate}%`} label="Oran" color="orange" />
                    </div>
                )}

                {/* Top Artists */}
                {stats && stats.top_artists.length > 0 && (
                    <div className="bg-white rounded-2xl p-4 mb-6">
                        <p className="text-sm font-medium text-gray-900 mb-3">🎤 Popüler Sanatçılar</p>
                        <div className="flex flex-wrap gap-2">
                            {stats.top_artists.slice(0, 8).map((a, i) => (
                                <span key={i} className="px-3 py-1.5 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">
                                    {a.artist} <span className="text-purple-400 ml-1">{a.count}</span>
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Search */}
                <div className="bg-white rounded-2xl p-4 mb-6">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Şarkı, sanatçı veya kullanıcı ara..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 bg-gray-50 rounded-xl text-sm text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-purple-500"
                        />
                    </div>
                    <p className="mt-2 text-xs text-gray-500">{filtered.length} sonuç</p>
                </div>

                {/* Music Grid - Album Style */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {filtered.slice(0, limit).map(m => (
                        <div key={m.id} className="bg-white rounded-2xl p-3 hover:ring-2 hover:ring-purple-200 transition-all cursor-pointer" onClick={() => setSelected(m)}>
                            {/* Album Art */}
                            <div className="aspect-square rounded-xl overflow-hidden mb-3 bg-gray-100 relative">
                                {m.image_url ? (
                                    <img src={m.image_url} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center">
                                        <Music className="w-8 h-8 text-white" />
                                    </div>
                                )}

                                {/* Play Button Overlay */}
                                {m.preview_url && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); playPreview(m.id, m.preview_url!); }}
                                        className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 flex items-center justify-center transition-opacity"
                                    >
                                        <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                                            {audioPlaying === m.id ? <Pause className="w-4 h-4 text-gray-900" /> : <Play className="w-4 h-4 text-gray-900 ml-0.5" />}
                                        </div>
                                    </button>
                                )}

                                {/* Playing Indicator */}
                                {audioPlaying === m.id && (
                                    <div className="absolute bottom-2 right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center animate-pulse">
                                        <Music className="w-3 h-3 text-white" />
                                    </div>
                                )}
                            </div>

                            {/* Info */}
                            <p className="text-sm font-medium text-gray-900 truncate">{m.track_name}</p>
                            <p className="text-xs text-gray-500 truncate">{m.track_artist}</p>

                            {/* Sender -> Receiver */}
                            <div className="flex items-center gap-1 mt-2 text-[10px] text-gray-400">
                                <span className="truncate">{m.sender_username}</span>
                                <ArrowRight className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate">{m.receiver_username}</span>
                            </div>

                            {/* Status */}
                            <div className="mt-2">
                                <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${m.is_played ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                    {m.is_played ? '✓ Dinlendi' : 'Bekliyor'}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Load More */}
                {filtered.length > limit && (
                    <div className="text-center mt-6">
                        <button onClick={() => setLimit(l => l + 50)} className="px-6 py-2.5 bg-purple-600 text-white rounded-xl text-sm font-medium hover:bg-purple-700 transition-colors">
                            Daha fazla yükle ({limit} / {filtered.length})
                        </button>
                    </div>
                )}

                {/* Empty */}
                {filtered.length === 0 && (
                    <div className="bg-white rounded-2xl p-12 text-center">
                        <Music className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">Müzik mesajı bulunamadı</p>
                    </div>
                )}
            </div>

            {/* Modal */}
            {selected && (
                <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50" onClick={() => setSelected(null)}>
                    <div className="bg-white w-full sm:max-w-md sm:rounded-2xl max-h-[90vh] overflow-auto rounded-t-3xl" onClick={e => e.stopPropagation()}>

                        {/* Handle */}
                        <div className="sm:hidden flex justify-center pt-2 pb-1">
                            <div className="w-9 h-1 bg-gray-300 rounded-full" />
                        </div>

                        {/* Header */}
                        <div className="px-5 py-4 flex items-center justify-between border-b border-gray-100">
                            <div className="flex items-center gap-3">
                                <div className="w-14 h-14 rounded-xl overflow-hidden bg-gradient-to-br from-purple-400 to-pink-500 flex-shrink-0">
                                    {selected.image_url ? (
                                        <img src={selected.image_url} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <Music className="w-6 h-6 text-white" />
                                        </div>
                                    )}
                                </div>
                                <div className="min-w-0">
                                    <p className="font-semibold text-gray-900 truncate">{selected.track_name}</p>
                                    <p className="text-sm text-gray-500 truncate">{selected.track_artist}</p>
                                    {selected.track_album && <p className="text-xs text-gray-400 truncate">{selected.track_album}</p>}
                                </div>
                            </div>
                            <button onClick={() => setSelected(null)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                                <X className="w-4 h-4 text-gray-500" />
                            </button>
                        </div>

                        {/* Status */}
                        <div className="px-5 pb-3 flex gap-2">
                            <span className={`px-2.5 py-1 rounded-xl text-xs font-medium ${selected.is_played ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                {selected.is_played ? '✓ Dinlendi' : 'Bekliyor'}
                            </span>
                            <span className={`px-2.5 py-1 rounded-xl text-xs font-medium ${selected.is_approved ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                                {selected.is_approved ? '✓ Onaylı' : 'Onay Bekliyor'}
                            </span>
                        </div>

                        {/* Details */}
                        <div className="px-5 pb-4 space-y-3">
                            <div className="p-3 bg-gray-50 rounded-xl">
                                <p className="text-xs text-gray-500 mb-1">Gönderen</p>
                                <p className="text-sm font-medium text-gray-900">{selected.sender_name || selected.sender_username}</p>
                                <p className="text-xs text-gray-500">@{selected.sender_username}</p>
                            </div>

                            <div className="p-3 bg-gray-50 rounded-xl">
                                <p className="text-xs text-gray-500 mb-1">Alıcı</p>
                                <p className="text-sm font-medium text-gray-900">{selected.receiver_name || selected.receiver_username}</p>
                                <p className="text-xs text-gray-500">@{selected.receiver_username}</p>
                            </div>

                            {selected.message && (
                                <div className="p-3 bg-purple-50 rounded-xl">
                                    <p className="text-xs text-purple-600 mb-1">Kişisel Mesaj</p>
                                    <p className="text-sm text-gray-900">{selected.message}</p>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-3">
                                <div className="p-3 bg-gray-50 rounded-xl">
                                    <p className="text-xs text-gray-500 mb-1">Gönderilme</p>
                                    <p className="text-sm text-gray-900">{new Date(selected.created_at).toLocaleDateString('tr-TR')}</p>
                                </div>
                                {selected.played_at && (
                                    <div className="p-3 bg-gray-50 rounded-xl">
                                        <p className="text-xs text-gray-500 mb-1">Dinlenme</p>
                                        <p className="text-sm text-gray-900">{new Date(selected.played_at).toLocaleDateString('tr-TR')}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="p-5 border-t border-gray-100 space-y-2">
                            {selected.external_url && (
                                <a
                                    href={selected.external_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full py-2.5 bg-green-500 text-white rounded-xl text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-green-600"
                                >
                                    <ExternalLink className="w-4 h-4" />
                                    Spotify'da Aç
                                </a>
                            )}
                            <button
                                onClick={() => handleDelete(selected.id)}
                                disabled={actionLoading === selected.id}
                                className="w-full py-2.5 bg-red-500 text-white rounded-xl text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-red-600"
                            >
                                {actionLoading === selected.id ? (
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                    <Trash2 className="w-4 h-4" />
                                )}
                                Sil
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MusicManagement;
